import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { format } from 'date-fns'
import { supabase } from '../config/supabase'

const { FiTrash2, FiPlus, FiEdit2 } = FiIcons

const AdminDashboard = () => {
  const { user } = useAuth()
  const [certificates, setCertificates] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [editingCert, setEditingCert] = useState(null)

  useEffect(() => {
    fetchCertificates()
  }, [])

  const fetchCertificates = async () => {
    setLoading(true)

    const { data, error } = await supabase
      .from('certificates') // ✅ CORRECT TABLE
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Fetch certificates error:', error)
    } else {
      setCertificates(data || [])
    }

    setLoading(false)
  }

  const handleDelete = async (certificate) => {
    const confirmDelete = window.confirm('Delete this certificate permanently?')
    if (!confirmDelete) return

    if (certificate.file_path) {
      await supabase.storage
        .from('coa-certificates') // ✅ STORAGE BUCKET
        .remove([certificate.file_path])
    }

    const { error } = await supabase
      .from('certificates') // ✅ CORRECT TABLE
      .delete()
      .eq('id', certificate.id)

    if (!error) {
      setCertificates(prev => prev.filter(c => c.id !== certificate.id))
    }
  }

  const filteredCertificates = certificates.filter(cert =>
    cert.lot_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.certificate_name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (!user) {
    return (
      <div className="p-8 text-center bg-white border rounded-lg">
        Access denied
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-semibold mb-2">Certificate Management</h1>
      <p className="text-gray-600 mb-6">Upload and manage product certificates</p>

      {/* Actions */}
      <div className="bg-white border rounded-lg p-4 mb-6 flex justify-between">
        <input
          type="text"
          placeholder="Search certificates..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="border px-3 py-2 rounded-lg w-80"
        />

        <button
          onClick={() => {
            setEditingCert(null)
            setShowUploadForm(true)
          }}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <SafeIcon icon={FiPlus} />
          Upload
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading…</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 text-sm">
              <tr>
                <th className="p-3 text-left">Lot</th>
                <th className="p-3 text-left">Certificate</th>
                <th className="p-3 text-center">Test Date</th>
                <th className="p-3 text-center">Uploaded</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCertificates.map(cert => (
                <tr key={cert.id} className="border-t">
                  <td className="p-3">{cert.lot_number}</td>
                  <td className="p-3">{cert.certificate_name}</td>
                  <td className="p-3 text-center">
                    {format(new Date(cert.test_date), 'MMM d, yyyy')}
                  </td>
                  <td className="p-3 text-center">
                    {format(new Date(cert.created_at), 'MMM d, yyyy')}
                  </td>
                  <td className="p-3 text-right space-x-3">
                    <button
                      onClick={() => {
                        setEditingCert(cert)
                        setShowUploadForm(true)
                      }}
                      className="text-gray-600"
                    >
                      <SafeIcon icon={FiEdit2} />
                    </button>

                    <button
                      onClick={() => handleDelete(cert)}
                      className="text-red-600"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showUploadForm && (
        <UploadCertificateModal
          onClose={() => {
            setShowUploadForm(false)
            setEditingCert(null)
          }}
          onUploaded={fetchCertificates}
          editingCert={editingCert}
        />
      )}
    </div>
  )
}

const UploadCertificateModal = ({ onClose, onUploaded, editingCert }) => {
  const [form, setForm] = useState({
    lot: editingCert?.lot_number || '',
    name: editingCert?.certificate_name || '',
    type: editingCert?.certificate_type || '',
    date: editingCert?.test_date || '',
    file: null
  })
  const [uploading, setUploading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setUploading(true)

    let filePath = editingCert?.file_path

    if (form.file) {
      if (editingCert?.file_path) {
        await supabase.storage
          .from('coa-certificates')
          .remove([editingCert.file_path])
      }

      filePath = `certificates/${form.lot}-${Date.now()}-${form.file.name}`

      await supabase.storage
        .from('coa-certificates')
        .upload(filePath, form.file, { upsert: true })
    }

    if (editingCert) {
      await supabase
        .from('certificates')
        .update({
          lot_number: form.lot,
          certificate_name: form.name,
          certificate_type: form.type,
          test_date: form.date,
          file_path: filePath
        })
        .eq('id', editingCert.id)
    } else {
      await supabase.from('certificates').insert({
        lot_number: form.lot,
        certificate_name: form.name,
        certificate_type: form.type,
        test_date: form.date,
        file_path: filePath
      })
    }

    setUploading(false)
    onUploaded()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg w-96 space-y-4">
        <h2 className="text-lg font-semibold">
          {editingCert ? 'Edit Certificate' : 'Upload Certificate'}
        </h2>

        <input
          required
          placeholder="Lot Number"
          value={form.lot}
          onChange={e => setForm({ ...form, lot: e.target.value })}
        />

        <input
          required
          placeholder="Certificate Name"
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
        />

        <input
          type="date"
          required
          value={form.date}
          onChange={e => setForm({ ...form, date: e.target.value })}
        />

        <input
          type="file"
          accept=".pdf"
          onChange={e => setForm({ ...form, file: e.target.files[0] })}
        />

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="border px-3 py-2 rounded-lg w-full">
            Cancel
          </button>

          <button
            type="submit"
            disabled={uploading}
            className="bg-primary-600 text-white px-3 py-2 rounded-lg w-full"
          >
            {uploading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminDashboard
